import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Brain,
  Target,
  Lightbulb,
  BookOpen,
  Award,
  Users,
  Zap,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Card } from '../shared/Card';
import { Button } from '../shared/Button';
import { Badge } from '../shared/Badge';
import { ProgressBar } from '../shared/ProgressBar';

interface StudentProfile {
  activities: string[];
  interests: string[];
  strengths: string[];
  goals: string[];
}

interface ApplicationTheme {
  id: string;
  title: string;
  description: string;
  narrative: string;
  strength: number;
  activities: string[];
  suggestedImprovements: string[];
  collegeAlignment: string[];
}

const mockProfile: StudentProfile = {
  activities: ['Science Club', 'Volunteer at Animal Shelter', 'Math Tutoring', 'Environmental Club'],
  interests: ['Biology', 'Environmental Science', 'Animal Welfare', 'Research'],
  strengths: ['Analytical Thinking', 'Empathy', 'Leadership', 'Problem Solving'],
  goals: ['Become a Veterinarian', 'Help Animals', 'Research Animal Behavior', 'Make a Difference']
};

const possibleThemes: ApplicationTheme[] = [
  {
    id: '1',
    title: 'The Compassionate Scientist',
    description: 'A student who combines scientific rigor with deep empathy for living beings',
    narrative: 'Your profile tells the story of someone who doesn\'t just love science, but uses it as a tool for compassion. From tutoring struggling math students to volunteering at animal shelters, you demonstrate that true scientific understanding comes from caring about the subjects you study.',
    strength: 92,
    activities: ['Science Club', 'Volunteer at Animal Shelter', 'Math Tutoring'],
    suggestedImprovements: [
      'Start a research project on animal behavior',
      'Shadow a veterinarian',
      'Create educational content about animal welfare'
    ],
    collegeAlignment: ['Pre-Veterinary Programs', 'Biology with Animal Focus', 'Environmental Science']
  },
  {
    id: '2',
    title: 'The Environmental Advocate',
    description: 'A passionate advocate for environmental conservation and sustainability',
    narrative: 'Your activities reveal a consistent commitment to environmental stewardship. You\'re not just interested in environmental science—you\'re actively working to create change through education, community involvement, and hands-on conservation efforts.',
    strength: 87,
    activities: ['Environmental Club', 'Science Club', 'Volunteer at Animal Shelter'],
    suggestedImprovements: [
      'Lead a school recycling initiative',
      'Document environmental projects with data',
      'Partner with local environmental organizations'
    ],
    collegeAlignment: ['Environmental Science', 'Conservation Biology', 'Sustainability Studies']
  },
  {
    id: '3',
    title: 'The Analytical Helper',
    description: 'Someone who uses analytical skills to solve problems and help others',
    narrative: 'You have a unique ability to break down complex problems and make them accessible to others. Whether it\'s tutoring math or organizing club activities, you consistently use your analytical mindset to create positive outcomes for your community.',
    strength: 79,
    activities: ['Math Tutoring', 'Science Club', 'Environmental Club'],
    suggestedImprovements: [
      'Create educational materials or apps',
      'Participate in math competitions',
      'Start a peer tutoring program'
    ],
    collegeAlignment: ['Applied Mathematics', 'Education', 'Data Science']
  }
];

export const ThemeBuilder: React.FC = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ApplicationTheme | null>(null);
  const [progress, setProgress] = useState(0);

  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setProgress(0);

    // Simulate AI analysis with progress updates
    const steps = [
      { message: 'Analyzing activities and experiences...', progress: 25 },
      { message: 'Identifying patterns and themes...', progress: 50 },
      { message: 'Generating narrative possibilities...', progress: 75 },
      { message: 'Calculating theme strength scores...', progress: 100 }
    ];

    for (const step of steps) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setProgress(step.progress);
    }

    setIsAnalyzing(false);
    setAnalysisComplete(true);
  };

  const selectTheme = (theme: ApplicationTheme) => {
    setSelectedTheme(theme);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <Brain className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">AI Application Theme Builder</h2>
          <p className="text-primary-100 text-lg mb-6">
            Discover your unique story and build a compelling application narrative
            that stands out to admissions officers
          </p>
          {!analysisComplete && (
            <Button
              onClick={runAnalysis}
              disabled={isAnalyzing}
              variant="secondary"
              className="bg-white text-primary-600 hover:bg-gray-50"
            >
              <Sparkles size={20} className="mr-2" />
              {isAnalyzing ? 'Analyzing Profile...' : 'Analyze My Profile'}
            </Button>
          )}
        </div>
      </Card>

      {/* Current Profile */}
      <Card>
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Your Current Profile</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <Target size={16} className="mr-2 text-primary-500" />
              Activities
            </h4>
            <div className="space-y-2">
              {mockProfile.activities.map((activity, index) => (
                <Badge key={index} variant="primary" size="sm">
                  {activity}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <Lightbulb size={16} className="mr-2 text-secondary-500" />
              Interests
            </h4>
            <div className="space-y-2">
              {mockProfile.interests.map((interest, index) => (
                <Badge key={index} variant="secondary" size="sm">
                  {interest}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <Award size={16} className="mr-2 text-accent-500" />
              Strengths
            </h4>
            <div className="space-y-2">
              {mockProfile.strengths.map((strength, index) => (
                <Badge key={index} variant="accent" size="sm">
                  {strength}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <BookOpen size={16} className="mr-2 text-primary-500" />
              Goals
            </h4>
            <div className="space-y-2">
              {mockProfile.goals.map((goal, index) => (
                <Badge key={index} variant="success" size="sm">
                  {goal}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Analysis Progress */}
      {isAnalyzing && (
        <Card>
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"
            />
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              AI Analyzing Your Profile...
            </h3>
            <ProgressBar progress={progress} color="primary" showPercentage className="mb-4" />
            <p className="text-gray-600">
              Our AI is identifying patterns in your activities and creating compelling narrative themes
            </p>
          </div>
        </Card>
      )}

      {/* Analysis Results */}
      <AnimatePresence>
        {analysisComplete && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Your Application Themes
                </h3>
                <Badge variant="success">
                  <CheckCircle size={14} className="mr-1" />
                  Analysis Complete
                </Badge>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {possibleThemes.map((theme, index) => (
                  <motion.div
                    key={theme.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    className={`relative cursor-pointer ${
                      selectedTheme?.id === theme.id ? 'ring-2 ring-primary-500' : ''
                    }`}
                    onClick={() => selectTheme(theme)}
                  >
                    <Card hover className="h-full">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {theme.title}
                        </h4>
                        <div className="flex items-center bg-primary-50 px-2 py-1 rounded-lg">
                          <Zap className="w-4 h-4 text-primary-500 mr-1" />
                          <span className="text-sm font-semibold text-primary-700">
                            {theme.strength}%
                          </span>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-4">
                        {theme.description}
                      </p>

                      <div className="mb-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">
                          Supporting Activities:
                        </h5>
                        <div className="flex flex-wrap gap-1">
                          {theme.activities.map((activity, idx) => (
                            <Badge key={idx} variant="accent" size="sm">
                              {activity}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="mt-auto">
                        <Button
                          variant={selectedTheme?.id === theme.id ? 'primary' : 'outline'}
                          size="sm"
                          className="w-full"
                        >
                          {selectedTheme?.id === theme.id ? 'Selected' : 'Choose Theme'}
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </Card>

            {/* Selected Theme Details */}
            {selectedTheme && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card>
                  <h3 className="text-xl font-semibold text-gray-900 mb-6">
                    Your Selected Theme: "{selectedTheme.title}"
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Your Narrative:</h4>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        {selectedTheme.narrative}
                      </p>

                      <h4 className="font-semibold text-gray-800 mb-3">
                        Aligned College Programs:
                      </h4>
                      <div className="space-y-2">
                        {selectedTheme.collegeAlignment.map((program, index) => (
                          <Badge key={index} variant="secondary">
                            {program}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">
                        Suggested Improvements:
                      </h4>
                      <ul className="space-y-3">
                        {selectedTheme.suggestedImprovements.map((improvement, index) => (
                          <li key={index} className="flex items-start">
                            <ArrowRight size={16} className="text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{improvement}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 p-4 bg-primary-50 rounded-lg">
                        <h5 className="font-semibold text-primary-800 mb-2">
                          Next Steps:
                        </h5>
                        <div className="flex flex-col gap-2">
                          <Button size="sm" className="w-full">
                            Build Essay Around This Theme
                          </Button>
                          <Button variant="outline" size="sm" className="w-full">
                            Find Activities to Strengthen Theme
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};